'use client';

import * as React from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/atoms/accordion';
import { RichText as RichTextConverter } from "@payloadcms/richtext-lexical/react";
import styles from './StepsAccordion.module.css';
import { StepContent, StepContentProps } from './StepContent';
import { ILargeAccordionBlock } from '@/page-builder/fragments/LargeAccordionBlock/types';

export interface ColorScheme {
  theme: 'green1' | 'green2' | 'green3' | 'green4' | 'terrain1' | 'terrain2' | 'terrain3' | 'terrain4';
  type: 'border' | 'background';
}

export interface StepData {
  id: string;
  title: any;
  content: StepContentProps;
  colorScheme: ColorScheme;
}

const themeClassMap: Record<
  "green1" | "green2" | "green3" | "green4" | "terrain1" | "terrain2" | "terrain3" | "terrain4",
  { bg: string; border: string; open: string }
> = {
  green1: {
    bg: 'bg-primary-100',
    border: 'border-primary-100',
    open: 'data-[state=open]:bg-primary-100 hover:bg-primary-100',
  },
  green2: {
    bg: 'bg-primary-200',
    border: 'border-primary-200',
    open: 'data-[state=open]:bg-primary-200 hover:bg-primary-200',
  },
  green3: {
    bg: 'bg-primary-300',
    border: 'border-primary-300',
    open: 'data-[state=open]:bg-primary-300 hover:bg-primary-300',
  },
  green4: {
    bg: 'bg-primary-400',
    border: 'border-primary-400',
    open: 'data-[state=open]:bg-primary-400 hover:bg-primary-400',
  },
  terrain1: {
    bg: 'bg-secondary-100',
    border: 'border-secondary-100',
    open: 'data-[state=open]:bg-secondary-100 hover:bg-secondary-100',
  },
  terrain2: {
    bg: 'bg-secondary-200',
    border: 'border-secondary-200',
    open: 'data-[state=open]:bg-secondary-200 hover:bg-secondary-200',
  },
  terrain3: {
    bg: 'bg-secondary-300',
    border: 'border-secondary-300',
    open: 'data-[state=open]:bg-secondary-300 hover:bg-secondary-300',
  },
  terrain4: {
    bg: 'bg-secondary-400',
    border: 'border-secondary-400',
    open: 'data-[state=open]:bg-secondary-400 hover:bg-secondary-400',
  },
};

export const StepsAccordionSection: React.FC<ILargeAccordionBlock> = ({ items }) => {
  return (
    <div className='pb-64 xl:pb-96'>
      <Accordion type="multiple" className="w-full">
        {items?.map((step, index) => {
          const theme = step.headerColor || 'green1';
          const type = 'border';
          const { bg, border, open } = themeClassMap[theme as keyof typeof themeClassMap];

          const colorClass =
            type === 'border'
              ? `border-b-4 xl:border-b-8 ${border} ${open}`
              : `${bg} ${open}`;

          const triggerClass = [
            colorClass,
            'hover:no-underline',
            'h-[96px] md:h-[106px] xl:h-[128px] transition-colors duration-300 ease-in-out',
          ].join(' ');

          return (
            <AccordionItem key={`step-${index}`} value={`step-${index}`} className="border-0">
              <AccordionTrigger className={triggerClass} hasContainer>
                <div className={styles.richTextWrapper + " text-start"}>
                  <RichTextConverter data={step.title as any} />
                </div>
              </AccordionTrigger>

              <AccordionContent className="pt-0 pb-0">
                <div className="container mx-auto text-[#2B363C]">
                  <StepContent
                    index={index + 1}
                    {...step}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
